import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, ChevronDown } from 'lucide-react';
import '../ig/ig-liste.css';
import '../recipes/recettes-couts.css';
import { avecLogos } from '../BrandLogos';

// Rail droit unique « 360 Resto » (patron Recettes & coûts) : boutons blancs à liseré gris, expert paramétrable.
export const RailBtn = ({ children, onClick, disabled, center, auto, arrow, testId, title, Icon, sub, danger, sep, count }) => (
  <button type="button" className={`rc-rail-btn ig-menu-item${center ? ' is-center' : ''}${auto ? ' is-auto' : ''}${danger ? ' is-danger' : ''}${sep ? ' is-sep' : ''}`} onClick={onClick} disabled={disabled} title={title} data-testid={testId} data-keepicon="">
    <span className="ig-menu-ico" aria-hidden>{Icon ? <Icon size={18} /> : null}</span>
    <span className="ig-menu-txt"><span className="ig-menu-l">{avecLogos(children)}</span>{sub && <small className="ig-menu-s">{avecLogos(sub, 13)}</small>}</span>
    {count != null && count !== '' ? <b className="ig-menu-n">{count}</b> : null}
    {arrow && <ArrowRight size={14} className="rc-rail-arrow" aria-hidden />}
  </button>
);

export const RailMenu = ({ label, items = [], disabled, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const k = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', h); document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [open]);
  return (
    <span className="rc-rail-menu" ref={ref}>
      <button type="button" className="rc-rail-btn is-auto" disabled={disabled} aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen((o) => !o)} data-testid={testId}>
        <ArrowUpRight size={14} aria-hidden /> {label} <ChevronDown size={13} aria-hidden />
      </button>
      {open && <div className="rc-rail-menu-list" role="menu" data-testid={`${testId}-menu`}>
        {items.filter(Boolean).map((it) => <button key={it.key} type="button" role="menuitem" disabled={it.disabled} onClick={() => { setOpen(false); it.onClick?.(); }} data-testid={`${testId}-${it.key}`}>{it.label}</button>)}
      </div>}
    </span>
  );
};

// Carte « Actions » : recherche en tête (slot `lead`) puis boutons.
export const RailActions = ({ lead, children, foot, testId = 'rail-actions' }) => (
  <section className="rc-rail-card ig-menu" data-testid={testId} aria-label="Actions du module">
    {lead ? <div className="ig-menu-grp is-lead">{lead}</div> : null}
    <div className="rc-rail-btns ig-menu-grp">{children}</div>
    {foot ? <p className="rc-rail-foot" data-testid={`${testId}-foot`}>{foot}</p> : null}
  </section>
);

// « EXPERT · N autres décisions » : pastille d'état + titre + ligne grise + bouton blanc.
export const ExpertDecisions = ({ name = 'Léo', items = [], onActions, vide = 'Rien d’autre à traiter.', testId = 'rail-decisions' }) => (
  <section className="rc-rail-card" data-testid={testId} aria-label={`Autres décisions de ${name}`}>
    <p className="rc-rail-kicker">{name}<small data-testid={`${testId}-n`}>· {items.length} autre{items.length > 1 ? 's' : ''} décision{items.length > 1 ? 's' : ''}</small></p>
    {items.length === 0
      ? <p className="rc-rail-foot" data-testid={`${testId}-vide`}>{vide}</p>
      : items.map((d) => (
        <div className="rc-dec" key={d.key} data-testid={`${testId}-${d.key}`}>
          <span className={`rc-dec-pill tone-${d.tone}`}>{d.pill}</span>
          <p className="rc-dec-title">{d.title}</p>
          {d.sub && <p className="rc-dec-sub">{d.sub}</p>}
          <RailBtn center onClick={d.onClick} disabled={d.disabled} testId={`${testId}-${d.key}-btn`}>{d.action}</RailBtn>
        </div>
      ))}
    {onActions && <button type="button" className="rc-rail-foot is-link" onClick={onActions} data-testid={`${testId}-actions`}>aussi dans Actions</button>}
  </section>
);

// « EXPERT vous conseille » : phrase issue des vraies données + « Demander à … » + bouton(s) complémentaire(s).
export const ExpertConseil = ({ expert = 'leo', name = 'Léo', text, extra, testId = 'rail-conseil' }) => (
  <section className="rc-rail-card" data-testid={testId} aria-label={`Le conseil de ${name}`}>
    <p className="rc-rail-kicker">{name}<small>vous conseille</small></p>
    {text && <div className="rc-conseil-quote" data-testid={`${testId}-text`}>« {text} »</div>}
    <div className="rc-rail-btns">
      <RailBtn onClick={() => window.dispatchEvent(new CustomEvent('sabril:open-agent', { detail: { key: expert } }))} testId={`${testId}-ask`}>Demander à {name}</RailBtn>
      {extra}
    </div>
  </section>
);
